import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ShoppingCart, PackageX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";
import { AppPromoCard } from "@/components/shop/AppPromoCard";
import { ProductImage } from "@/components/shop/ProductImage";

const formatXOF = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n) || 0) + " F";

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState({});
  const { user, addToCart } = useShop();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prodRes, catRes] = await Promise.all([
        api.products({ search: q || undefined, category: category || undefined, limit: 60 }),
        api.categories().catch(() => ({ categories: [] })),
      ]);
      setProducts(prodRes?.products || []);
      setCategories(catRes?.categories || []);
    } catch (e) {
      setError(e.message || "Impossible de charger le catalogue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  // Debounced search — refetch 350ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleAdd = async (p) => {
    if (!user) {
      toast.info("Connectez-vous pour ajouter au panier.");
      navigate("/compte/connexion?next=/catalogue");
      return;
    }
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      await addToCart(p.id, 1);
      toast.success(`« ${p.name} » ajouté au panier.`);
    } catch (e) {
      toast.error(e.message || "Ajout impossible.");
    } finally {
      setBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10 md:py-16">
      <Helmet>
        <title>Catalogue Zafara Bénin — Achetez local et livré partout au Bénin</title>
        <meta
          name="description"
          content="Découvrez le catalogue Zafara : produits, mode, tech, épicerie et plus, livrés partout au Bénin. Paiement Mobile Money (MTN, Moov) ou à la livraison."
        />
        <link rel="canonical" href="https://zafarabenin.fr/catalogue" />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Catalogue Zafara Bénin" />
        <meta property="og:description" content="Achetez local, livré partout au Bénin — paiement Mobile Money." />
        <meta property="og:url" content="https://zafarabenin.fr/catalogue" />
        <meta property="og:image" content="https://zafarabenin.fr/og-image.png" />
        {products.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              itemListElement: products.slice(0, 30).map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://zafarabenin.fr/produit/${p.id}`,
                name: p.name,
              })),
            })}
          </script>
        )}
      </Helmet>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">Catalogue</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl text-[#16241B] tracking-tighter">
            Tous les produits <span className="text-[#0E7A3B]">Zafara</span>
          </h1>
          <p className="mt-3 text-[#16241B]/70 max-w-xl">
            Produits en temps réel depuis l'application Zafara. Ajoutez au panier et commandez en quelques clics.
          </p>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="relative w-full md:w-96"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#16241B]/50" />
          <input
            data-testid="catalogue-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un produit…"
            className="w-full rounded-full border border-[#EDE7D9] bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </form>
      </div>

      {categories.length > 0 && (
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            onClick={() => setCategory("")}
            className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${
              !category ? "bg-[#0E7A3B] text-white border-[#0E7A3B]" : "bg-white border-[#EDE7D9] text-[#16241B]/75 hover:border-[#0E7A3B]"
            }`}
          >
            Toutes
          </button>
          {categories.map((c) => {
            const name = typeof c === "string" ? c : c.name;
            return (
              <button
                key={name}
                onClick={() => setCategory(name)}
                data-testid={`catalogue-cat-${name}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${
                  category === name ? "bg-[#0E7A3B] text-white border-[#0E7A3B]" : "bg-white border-[#EDE7D9] text-[#16241B]/75 hover:border-[#0E7A3B]"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-10">
        <AppPromoCard />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 text-[#16241B]/60">
          <Loader2 className="h-6 w-6 animate-spin mr-3" /> Chargement du catalogue…
        </div>
      )}
      {error && (
        <div data-testid="catalogue-error" className="rounded-2xl border border-[#E8112D]/30 bg-[#E8112D]/5 p-6 text-[#E8112D]">
          {error}
        </div>
      )}
      {!loading && !error && products.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#EDE7D9] p-12 text-center">
          <PackageX className="h-8 w-8 mx-auto text-[#16241B]/30" />
          <p className="mt-4 text-sm text-[#16241B]/60">Aucun produit disponible pour le moment.</p>
        </div>
      )}

      <div data-testid="catalogue-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((p) => {
          const outOfStock = (p.stock ?? 1) <= 0;
          return (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="group rounded-3xl bg-white border border-[#EDE7D9] overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(14,122,59,0.1)] transition-all duration-300"
            >
              <Link to={`/produit/${p.id}`} className="block">
                <div className="aspect-square bg-[#FBFAF6] overflow-hidden">
                  <ProductImage
                    src={p.image_url}
                    alt={p.name}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Link>
              <div className="p-4">
                <Link to={`/produit/${p.id}`}>
                  <h3 className="font-display font-semibold text-base leading-tight text-[#16241B] line-clamp-2 hover:text-[#0E7A3B] transition-colors">
                    {p.name}
                  </h3>
                </Link>
                <div className="mt-3 flex items-center justify-between">
                  <p className="font-display font-bold text-lg text-[#0E7A3B]">{formatXOF(p.price)}</p>
                  <span className={`text-[10px] font-utility uppercase tracking-widest ${outOfStock ? "text-[#E8112D]" : "text-[#0E7A3B]"}`}>
                    {outOfStock ? "Rupture" : "En stock"}
                  </span>
                </div>
                <button
                  data-testid={`product-add-${p.id}`}
                  onClick={() => handleAdd(p)}
                  disabled={outOfStock || busy[p.id]}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#16241B] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#0E7A3B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy[p.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  {outOfStock ? "Indisponible" : "Ajouter au panier"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
